import { UserOutlined, SettingOutlined, LogoutOutlined, DownOutlined } from '@ant-design/icons'
import { Dropdown, Avatar, Button, Space } from 'antd'
import type { MenuProps } from 'antd'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { TRANSITIONS } from '@/shared/constants/transitions'
import { useThemeColors } from '@/shared/hooks/useThemeColors'
import { UserMenuProps } from '@/types/layout'

export const UserMenu: React.FC<UserMenuProps> = ({ user }) => {
  const navigate = useNavigate()
  const colors = useThemeColors()
  const { logout } = useAuth()

  // Generate initials from name
  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  // Truncate name if too long
  const truncateName = (name: string, maxLength: number = 25): string => {
    if (name.length <= maxLength) return name
    return name.substring(0, maxLength) + '...'
  }

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'settings':
        navigate('/settings')
        break
      case 'logout':
        logout()
        break
    }
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'settings',
      label: 'Cài đặt',
      icon: <SettingOutlined style={{ fontSize: 16 }} />,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Đăng xuất',
      icon: <LogoutOutlined style={{ fontSize: 16 }} />,
      danger: true,
    },
  ]

  return (
    <Dropdown 
      menu={{ items: menuItems, onClick: handleMenuClick }} 
      placement="bottomRight"
      trigger={['click']}
    >
      <Button
        type="text"
        style={{
          height: 'auto',
          padding: '6px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          border: '1px solid transparent',
          borderRadius: '12px',
          transition: TRANSITIONS.THEME
        }}
        onMouseEnter={(e) => {
          const target = e.currentTarget as HTMLElement
          target.style.backgroundColor = colors.backgroundHover
          target.style.borderColor = colors.border
        }}
        onMouseLeave={(e) => {
          const target = e.currentTarget as HTMLElement
          target.style.backgroundColor = 'transparent'
          target.style.borderColor = 'transparent'
        }}
      >
        {/* Avatar */}
        {user.avatar ? (
          <Avatar 
            src={user.avatar} 
            size={40}
            style={{ 
              border: '2px solid #00A19B',
              flexShrink: 0
            }}
          />
        ) : (
          <Avatar 
            size={40}
            style={{ 
              backgroundColor: '#00A19B',
              background: 'linear-gradient(135deg, #00A19B 0%, #04BFAD 100%)',
              border: '2px solid rgba(4, 191, 173, 0.3)',
              fontWeight: 600,
              flexShrink: 0
            }}
          >
            {getInitials(user.name)}
          </Avatar>
        )}

        {/* Name (Hidden on mobile) */}
        <div 
          className="hidden md:block" 
          style={{ 
            textAlign: 'left',
            minWidth: 0,
            flex: 1
          }}
        >
          <div 
            style={{ 
              color: colors.textPrimary,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              transition: TRANSITIONS.COLOR
            }}
          >
            {truncateName(user.name)}
          </div>
          {user.role && (
            <div 
              style={{ 
                color: colors.textSecondary,
                fontSize: '12px',
                lineHeight: '16px',
                marginTop: '2px',
                transition: TRANSITIONS.COLOR
              }}
            >
              {user.role}
            </div>
          )}
        </div>

        {/* Dropdown Icon (Hidden on mobile) */}
        <DownOutlined 
          className="hidden md:block"
          style={{ 
            fontSize: 12, 
            color: colors.textSecondary,
            flexShrink: 0,
            transition: TRANSITIONS.COLOR
          }} 
        />
      </Button>
    </Dropdown>
  )
}

export default UserMenu
